import * as React from "react";
import { Box, GridItem, Heading } from "@chakra-ui/react";
import { Form, Grid, NumberInput, Select, TextInput } from "components";
import { useCurrentCase } from "duck";
import { AnesthesiaSystemEnum } from "requests";
import * as duck from "./duck";

const Miscellaneous: React.FC = () => {
  const miscellaneous = useCurrentCase(
    medicalCase => medicalCase.data.client.patient.miscellaneousGroup
  );
  const patientWeight = useCurrentCase(
    medicalCase => medicalCase.data.client.patient.weight || 0
  );

  const validation = React.useMemo(() => duck.getValidation(miscellaneous), [
    miscellaneous
  ]);

  const update = duck.useUpdate(miscellaneous._id);

  return (
    <Box>
      <Heading mb={4} as="h3" color="black" fontSize="lg" lineHeight="7">
        Miscellaneous
      </Heading>

      <Form<duck.FormValues> {...validation}>
        {({ setValue }) => (
          <>
            <Form.Watcher
              onChange={update}
              defaultValues={validation.defaultValues}
            />
            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)"
              }}
            >
              <GridItem>
                <Select
                  name="catheterSize"
                  label="IV catheter size"
                  placeholder="Select IV catheter size"
                  options={duck.CATHETER_SIZE_OPTIONS}
                />
              </GridItem>
              <GridItem>
                <Select
                  name="catheterLocation"
                  label="IV catheter location"
                  placeholder="Select IV catheter location"
                  options={duck.CATHETER_LOCATION_OPTIONS}
                />
              </GridItem>
              <GridItem>
                <Select
                  name="fluidType"
                  label="Fluid Type"
                  placeholder="Select Fluid Type"
                  options={duck.FLUID_TYPE_OPTIONS}
                />
              </GridItem>
              <GridItem>
                <Select
                  name="bloodPressureMonitoring"
                  label="Blood Pressure Monitoring"
                  placeholder="Select Blood Pressure Monitoring"
                  options={duck.BLOOD_PRESSURE_MONITORING}
                />
              </GridItem>
              <GridItem>
                <Select
                  creatable
                  name="fluidRate"
                  label="Fluid Rate"
                  placeholder="Select Fluid Rate"
                  options={duck.FLUID_RATE_OPTIONS}
                  getNewOptionLabel={value => `${value} mL/kg/hour`}
                  isValidNewOption={inputValue => {
                    if (
                      inputValue.includes("-") ||
                      inputValue.length === 0 ||
                      Number.isNaN(+inputValue)
                    ) {
                      return false;
                    }
                    return true;
                  }}
                  onChange={value => {
                    setValue("fluidRate", value);
                    const newRate = value === null ? 0 : +value * patientWeight;
                    setValue("fluidRateValue", `${newRate}`);
                  }}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  min={0}
                  mt="8"
                  name="fluidRateValue"
                  label="Fluid Rate Value"
                  labelHidden
                  placeholder="Enter Fluid Rate"
                  getValueLabel={value => `${value} mls/hr`}
                />
              </GridItem>
              <GridItem>
                <Select
                  label="Quality of sedation"
                  name="qualityOfSedation"
                  placeholder="Select Quality of sedation"
                  options={duck.QUALITY_OF_SEDATION_OPTIONS}
                />
              </GridItem>
              <GridItem>
                <Select<AnesthesiaSystemEnum>
                  label="Anesthesia System"
                  name="anesthesiaSystem"
                  placeholder="Select Anesthesia System"
                  options={duck.ANESTHESIA_SYSTEM_OPTIONS}
                  onChange={value => {
                    setValue("anesthesiaSystem", value);
                    const newOxygenFlowRate =
                      value === AnesthesiaSystemEnum.Rebreathing
                        ? patientWeight * 30
                        : value === AnesthesiaSystemEnum.NonRebreathing
                        ? patientWeight * 200
                        : null;
                    setValue(
                      "oxygenFlowRate",
                      newOxygenFlowRate === null
                        ? null
                        : +newOxygenFlowRate.toFixed(3)
                    );
                  }}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  min={0}
                  label="Bag Size (Liters)"
                  name="bagSize"
                  placeholder="Enter Bag Size (Liters)"
                  getValueLabel={value => `${value} Liter(s)`}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  min={0}
                  label="Oxygen Flow Rate"
                  name="oxygenFlowRate"
                  placeholder="Enter Oxygen Flow Rate"
                  getValueLabel={value => `${value} mL/min`}
                />
              </GridItem>
              <GridItem>
                <Select
                  label="Intubated?"
                  name="intubated"
                  placeholder="Select"
                  options={duck.INTUBATED_OPTIONS}
                />
              </GridItem>
              <GridItem>
                <TextInput
                  label="Endotracheal Tube Size"
                  name="endotrachealTubeSize"
                  placeholder="Enter Endotracheal Tube Size"
                />
              </GridItem>
            </Grid>
          </>
        )}
      </Form>
    </Box>
  );
};

export default Miscellaneous;
